// Support für Kunden: Anfragen stellen, Antworten lesen, nachfragen.
//
// Für alle Tarife — wer zahlt, muss fragen können. Die Seite ist in allen
// Sprachen der App, die Antworten kommen vom Anbieter auf Deutsch.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import { useBenachrichtigungen } from '../../aufgaben/lib/benachrichtigungen';
import { datumZeit } from '../../../gemeinsam/lib/daten';
import type { Anhang } from '../../firma/lib/dateien';
import { Abzeichen, Auswahl, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import { AnhangWahl, SupportVerlauf, type SupportNachricht } from '../komponenten/SupportVerlauf';

type Status = 'offen' | 'in_arbeit' | 'wartet' | 'erledigt';
type Kategorie = 'frage' | 'fehler' | 'wunsch' | 'abrechnung' | 'sonstiges';

type Kurz = {
  id: string; nummer: string; betreff: string; kategorie: Kategorie; status: Status;
  anzahlNachrichten: number; ungelesen: boolean; updatedAt: string;
  /** Wer das Gespräch angefangen hat — der Betrieb selbst oder LagerPro. */
  herkunft?: 'kunde' | 'anbieter';
  letzteNachricht: { von: 'kunde' | 'support'; text: string; zeit: string } | null;
};
type Voll = Kurz & { nachrichten: SupportNachricht[] };

const KATEGORIEN: Kategorie[] = ['frage', 'fehler', 'wunsch', 'abrechnung', 'sonstiges'];

const STATUS_SCHLUESSEL: Record<Status, Schluessel> = {
  offen: 'support.status.offen', in_arbeit: 'support.status.in_arbeit',
  wartet: 'support.status.wartet', erledigt: 'support.status.erledigt',
};
const KATEGORIE_SCHLUESSEL: Record<Kategorie, Schluessel> = {
  frage: 'support.kat.frage', fehler: 'support.kat.fehler', wunsch: 'support.kat.wunsch',
  abrechnung: 'support.kat.abrechnung', sonstiges: 'support.kat.sonstiges',
};
// Aus Kundensicht: „wartet" heißt, es liegt eine Antwort für ihn bereit.
const STATUS_FARBE: Record<Status, 'neutral' | 'gelb' | 'blau' | 'gruen'> = {
  offen: 'neutral', in_arbeit: 'gelb', wartet: 'blau', erledigt: 'gruen',
};

export default function Support() {
  const { t } = useSprache();
  const { neuLaden: glockeNeu } = useBenachrichtigungen();
  const [liste, setListe] = useState<Kurz[] | null>(null);
  const [offen, setOffen] = useState<Voll | null>(null);
  const [neu, setNeu] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await api<{ anfragen: Kurz[] }>('/support');
      setListe(d.anfragen);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Fehler');
      setListe([]);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  async function oeffnen(id: string) {
    setFehler(null); setMeldung(null);
    try {
      setOffen(await api<Voll>(`/support/${id}`));
      // Öffnen markiert gelesen — Liste und Glocke nachziehen.
      void laden(); void glockeNeu();
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Fehler'); }
  }

  if (offen) {
    return <Detail anfrage={offen}
                   beiZurueck={() => { setOffen(null); void laden(); }}
                   beiAktualisiert={setOffen} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('support.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('support.untertitel')}</p>
        </div>
        {!neu && <Knopf variante="primaer" onClick={() => { setNeu(true); setMeldung(null); }}>{t('support.neu')}</Knopf>}
      </div>

      <Erfolgsmeldung text={meldung} />
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      {neu && (
        <NeueAnfrage
          beiAbbruch={() => setNeu(false)}
          beiErfolg={async (nummer) => {
            setNeu(false);
            setMeldung(t('support.gesendet').replace('{nummer}', nummer));
            await laden();
          }} />
      )}

      <Karte>
        {liste === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : liste.length === 0 ? (
          <LeerZustand titel={t('support.leer')} text={t('support.leerText')} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(a => (
              <li key={a.id}>
                <button onClick={() => oeffnen(a.id)}
                        className="flex w-full items-start gap-3 px-5 py-3.5 text-start hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${a.ungelesen ? 'bg-marke-600' : 'bg-transparent'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={`font-medium text-slate-900 dark:text-slate-100 ${a.ungelesen ? 'font-semibold' : ''}`}>{a.betreff}</span>
                      <Abzeichen art={STATUS_FARBE[a.status]}>{t(STATUS_SCHLUESSEL[a.status])}</Abzeichen>
                      {/* Ohne diesen Hinweis wundert sich der Kunde über ein
                          Gespräch, das er nie angefangen hat. */}
                      {a.herkunft === 'anbieter' && <Abzeichen art="blau">{t('support.vonAnbieter')}</Abzeichen>}
                    </span>
                    <span className="mt-0.5 block truncate text-sm text-slate-500 dark:text-slate-400">
                      {a.letzteNachricht?.von === 'support' ? `${t('support.team')}: ` : ''}{a.letzteNachricht?.text}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-400">
                      {a.nummer} · {t(KATEGORIE_SCHLUESSEL[a.kategorie])} · {datumZeit(a.updatedAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}

function NeueAnfrage({ beiAbbruch, beiErfolg }: { beiAbbruch: () => void; beiErfolg: (nummer: string) => Promise<void> }) {
  const { t } = useSprache();
  const [betreff, setBetreff] = useState('');
  const [kategorie, setKategorie] = useState<Kategorie>('frage');
  const [text, setText] = useState('');
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function senden(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true); setFehler(null);
    try {
      const r = await api<{ anfrage: { nummer: string } }>('/support', {
        methode: 'POST', daten: { betreff, kategorie, text, dateiIds: anhaenge.map(a => a.id) },
      });
      await beiErfolg(r.anfrage.nummer);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Fehler');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
      <Karte klasse="border-marke-300 dark:border-marke-800">
        <KartenKopf titel={t('support.neu')} />
        <form onSubmit={senden} className="space-y-4 p-5">
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
            <Feld label={t('support.betreff')} value={betreff} onChange={e => setBetreff(e.target.value)} required autoFocus maxLength={200} />
            <Auswahl label={t('support.kategorie')} value={kategorie} onChange={e => setKategorie(e.target.value as Kategorie)}>
              {KATEGORIEN.map(k => <option key={k} value={k}>{t(KATEGORIE_SCHLUESSEL[k])}</option>)}
            </Auswahl>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{t('support.nachricht')}</span>
            <textarea value={text} onChange={e => setText(e.target.value)} required rows={6} maxLength={10000}
                      placeholder={t('support.nachrichtPlatzhalter')}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
          </label>
          <div>
            <AnhangWahl anhaenge={anhaenge} setAnhaenge={setAnhaenge} beschriftung={t('support.anhang')} beiFehler={setFehler} />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('support.anhangHinweis')}</p>
          </div>
          <div className="flex gap-2">
            <Knopf type="submit" variante="primaer" laedt={sendet}>{t('support.senden')}</Knopf>
            <Knopf type="button" onClick={beiAbbruch}>{t('aktion.abbrechen')}</Knopf>
          </div>
        </form>
      </Karte>
    </Blickfang>
  );
}

function Detail({ anfrage, beiZurueck, beiAktualisiert }: {
  anfrage: Voll; beiZurueck: () => void; beiAktualisiert: (a: Voll) => void;
}) {
  const { t } = useSprache();
  const [text, setText] = useState('');
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function antworten(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSendet(true); setFehler(null);
    try {
      const r = await api<{ anfrage: Voll }>(`/support/${anfrage.id}/nachricht`, {
        methode: 'POST', daten: { text, dateiIds: anhaenge.map(a => a.id) },
      });
      beiAktualisiert(r.anfrage);
      setText(''); setAnhaenge([]);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Fehler');
    } finally { setSendet(false); }
  }

  async function erledigen() {
    try {
      await api(`/support/${anfrage.id}/erledigt`, { methode: 'POST' });
      beiAktualisiert({ ...anfrage, status: 'erledigt' });
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Fehler'); }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <button onClick={beiZurueck} className="text-sm text-marke-700 hover:underline dark:text-marke-300">
        ← {t('support.zurueck')}
      </button>

      <Karte>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{anfrage.betreff}</h1>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {anfrage.nummer} · {t(KATEGORIE_SCHLUESSEL[anfrage.kategorie])}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Abzeichen art={STATUS_FARBE[anfrage.status]}>{t(STATUS_SCHLUESSEL[anfrage.status])}</Abzeichen>
            {anfrage.status !== 'erledigt' && (
              <Knopf variante="still" className="!text-xs" onClick={erledigen}>{t('support.erledigt')}</Knopf>
            )}
          </div>
        </div>

        <div className="p-5">
          <SupportVerlauf nachrichten={anfrage.nachrichten} sicht="kunde"
                          anhangPfad={id => apiUrl(`/api/support/${anfrage.id}/datei/${id}`)}
                          beschriftung={{ eigene: t('support.sie'), fremde: t('support.team') }} />
        </div>

        {anfrage.status === 'erledigt' && (
          <p className="border-t border-slate-200 px-5 py-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-300">
            {t('support.erledigtHinweis')}
          </p>
        )}

        <form onSubmit={antworten} className="space-y-3 border-t border-slate-200 p-5 dark:border-slate-800">
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3} maxLength={10000}
                    placeholder={t('support.antwortPlatzhalter')}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AnhangWahl anhaenge={anhaenge} setAnhaenge={setAnhaenge} beschriftung={t('support.anhaengen')} beiFehler={setFehler} />
            <Knopf type="submit" variante="primaer" laedt={sendet} disabled={!text.trim()}>{t('support.antworten')}</Knopf>
          </div>
        </form>
      </Karte>
    </div>
  );
}
